import { Controller } from '@hotwired/stimulus';

import {
  currentSection,
  docBodyRequiresAnswerCommentInDom,
  extractAnswerCommentSiblingHtml,
  extractHiddenAnswerCommentHTML,
  getCsrfToken,
  getDusFromDom,
  hasAnswerCommentAvailable,
  removeAnswerCommentFromUI,
  restoreAnswerCommentHTML,
  shouldPreserveAnswerCommentInDom,
  snapshotAnswerCommentSiblingHtml,
  updateDocumentUserState,
  waitForVisible,
  noteSaveEpochsSettled,
  removeSpeechToTextToolbarFromContainer,
  findSubmitButtonContainerForElement,
  seemsOffline,
  OFFLINE_AI_NOTICE_BODY,
} from '../utils/case_experience_in_rails';
import { escapeHtml } from '../src/react/helpers/utils';

import smile1 from 'images/smile-rating-1.svg';
import smile2 from 'images/smile-rating-2.svg';
import smile3 from 'images/smile-rating-3.svg';
import smile4 from 'images/smile-rating-4.svg';
import smile5 from 'images/smile-rating-5.svg';

const MAX_SUMMARY_STATEMENT_TIME_MS = 60000;

export default class extends Controller {
  static targets = [
    'aiSummaryFeedbackBlock',
    'summaryFeedbackStream',
    'textarea',
    'infoModal',
    'infoModalOkay',
    'errorModal',
    'errorMessage',
    'gradedFeedback',
    'container',
    'dynamicButtonPlaceholder',
    'charCountWrapper',
    'disclaimer',
  ];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    feedbackState: Object,
    characterLimit: Number,
  };

  // --- AI Summary Feedback State ---
  aiFeedbackState = {
    requested: false,
    loading: false,
    received: false,
    error: null,
    feedback: '',
    userRating: 0,
    userComment: '',
    userRatingSubmitted: false,
  };

  _hasPersistedAiSummaryResponseError() {
    const errorRaw = this.feedbackStateValue?.aiSummaryStatementResponseError?.value;
    return errorRaw != null && String(errorRaw).trim().length > 0;
  }

  _shouldRequestAiFeedback() {
    return !this._hasPersistedAiSummaryResponseError();
  }

  _isAiFeedbackRequested() {
    return this._aiFeedbackRequested !== false;
  }

  applyDefaultAiFeedbackMode() {
    const shouldRequestFeedback = this._shouldRequestAiFeedback();
    this._aiFeedbackRequested = shouldRequestFeedback;
    if (shouldRequestFeedback && !this.aiFeedbackState.received) {
      this.setAiFeedbackRequested(true, { persist: true });
    } else if (!shouldRequestFeedback) {
      this.setAiFeedbackRequested(false, { persist: false });
    }
  }

  storeAnswerCommentHTML() {
    let result = extractHiddenAnswerCommentHTML(this.element);
    if (result) {
      this.answerCommentHtml = result.html;
      return;
    }
    if (docBodyRequiresAnswerCommentInDom(this.element)) {
      const snapshot = snapshotAnswerCommentSiblingHtml(this.element);
      this.answerCommentHtml = snapshot ? snapshot.html : null;
      return;
    }
    result = extractAnswerCommentSiblingHtml(this.element);
    this.answerCommentHtml = result ? result.html : null;
  }

  getAnswerCommentRootFromMemory() {
    if (!this.answerCommentHtml) return null;
    const doc = new DOMParser().parseFromString(this.answerCommentHtml, 'text/html');
    return doc.querySelector('.doc-section.answer_comment');
  }

  getAnswerCommentIdFromStoredHtml() {
    const root = this.getAnswerCommentRootFromMemory();
    return root?.id || null;
  }

  extractIdealFromAnswerCommentRoot(root) {
    if (!root) return '';
    const sectionBody = root.querySelector('.doc-section-body');
    if (!sectionBody) return '';
    const docChildren = sectionBody.querySelector('.doc-children');
    if (!docChildren) return '';
    const firstParagraph = docChildren.querySelector('.doc-paragraph');
    return firstParagraph ? firstParagraph.innerText.trim() : '';
  }

  restoreAnswerCommentHTMLFromVariable() {
    restoreAnswerCommentHTML(this.element, this.answerCommentHtml);
  }

  isStudentDisplayMode() {
    const docBody = this.element.closest('.doc-body');
    return Boolean(docBody && docBody.classList.contains('student-display-mode'));
  }

  connect() {
    this._suppressSummaryFeedbackAbortError = false;
    // Cache the inline modal; VPE cleanup globally removes .ai-error-modal, deleting ours.
    this._errorModalHtml = this.hasErrorModalTarget ? this.errorModalTarget.outerHTML : null;
    this.userSectionStateValue = getDusFromDom() || {};
    this._aiFeedbackRequested = this._shouldRequestAiFeedback();

    waitForVisible(currentSection(this.element), () => this.applyDefaultAiFeedbackMode());

    if (!shouldPreserveAnswerCommentInDom()) {
      this.storeAnswerCommentHTML();
    }

    // Update the aiFeedbackState object
    this.state = this.feedbackStateValue || {};
    // Initialize aiFeedbackState from backend state if present
    this.aiFeedbackState = {
      ...this.aiFeedbackState,
      requested: !!this.state.aiSummaryStatementRequestFeedback?.value,
      loading: !!this.state.aiSummaryStatementLoadingFeedback?.value,
      received: false,
      error: this.state.aiSummaryStatementResponseError?.value || null,
      feedback: this.state.aiSummaryStatementFeedback?.value || '',
      userRating: this.state.aiSummaryStatementUserRating?.value || 0,
      userComment: this.state.aiSummaryStatementUserComment?.value || '',
      userRatingSubmitted: !!this.state.aiSummaryStatementUserRatingSubmitted?.value,
    };
    const feedbackRaw = this.feedbackStateValue?.aiSummaryStatementFeedback?.value;
    const hasFeedbackContent =
      feedbackRaw != null && String(feedbackRaw).trim().length > 0;

    if (
      !shouldPreserveAnswerCommentInDom() &&
      this.feedbackStateValue?.graded?.value &&
      this.answerCommentHtml &&
      !hasFeedbackContent
    ) {
      this.restoreAnswerCommentHTMLFromVariable();
    }

    if (hasFeedbackContent) {
      this.aiFeedbackState.received = true;
      this.aiFeedbackState.feedback = feedbackRaw;
      this.renderStimulusTwoColumnFeedback(this.aiFeedbackState.feedback);
      this.applyChromeForSummaryFeedbackLayout();
      this.finalizeAnswerCommentPlacement();
      this.configureSubmitAfterFeedback();
      this.autoResizeFeedbackTextarea(this.aiSummaryFeedbackBlockTarget);
      if (this.aiFeedbackState.userRatingSubmitted) {
        this.disableRating();
        this.disableSubmitIfNextContinueIsEnabledOrNextSectionIsVisible();
      } else {
        this.enableRating();
        const submitButton = this.getSiblingButton();
        if (submitButton) {
          setTimeout(() => {
            const ratingGiven =
              this.aiFeedbackState.userRating && this.aiFeedbackState.userRating > 0;
            submitButton.disabled = !ratingGiven;
          }, 0);
        }
      }
    } else {
      // Hide gradedFeedback initially
      if (this.hasGradedFeedbackTarget) {
        this.gradedFeedbackTarget.style.display = 'none';
      }
      // Only disable submit button if textarea is empty (graded = already submitted; keep disabled)
      const submitButton = this.getSiblingButton();
      if (submitButton) {
        setTimeout(() => {
          if (this.feedbackStateValue?.graded?.value) {
            submitButton.disabled = true;
          } else {
            submitButton.disabled = !(
              this.textareaTarget &&
              this.textareaTarget.value &&
              this.textareaTarget.value.trim().length > 0
            );
          }
        }, 0);
      }
    }
    const answerCommentSibling = this.getAnswerCommentSiblingElement();
    const answerCommentBody =
      answerCommentSibling && answerCommentSibling.querySelector('.doc-section-body');
    if (
      this.isStudentDisplayMode() &&
      answerCommentBody &&
      answerCommentBody.offsetParent !== null
    ) {
      if (this.hasDisclaimerTarget && this.disclaimerTarget) {
        this.disclaimerTarget.style.display = 'none';
      }
      // Disable the original submit button
      const submitButton = this.getSiblingButton();
      if (submitButton) {
        setTimeout(() => {
          submitButton.disabled = true;
        }, 0);
      }
    }

    // Add event listener for normal flow submit button
    this._submitButton = this.getSiblingButton();
    this._handleSubmitClick = () => {
      if (
        this.aiFeedbackState.received &&
        this.aiFeedbackState.userRating > 0 &&
        !this.aiFeedbackState.userRatingSubmitted
      ) {
        const now = Date.now();
        void updateDocumentUserState({
          documentId: this.documentIdValue,
          userId: this.userIdValue,
          body: {
            document_user_state: {
              state: {
                [this.nodeUidValue]: {
                  aiSummaryStatementUserRating: {
                    value: this.aiFeedbackState.userRating,
                    updatedAt: now,
                  },
                  aiSummaryStatementUserComment: {
                    value: this.aiFeedbackState.userComment || '',
                    updatedAt: now,
                  },
                  aiSummaryStatementUserRatingSubmitted: { value: true, updatedAt: now },
                },
              },
            },
          },
        }).then(() => {
          this.aiFeedbackState.userRatingSubmitted = true;
          removeSpeechToTextToolbarFromContainer(this.element);
        });
      }
      setTimeout(() => {
        if (!this.element.isConnected) return;
        this.disableRating();
        if (!this._isAiFeedbackRequested()) {
          this.restoreAnswerCommentHTMLFromVariable();
          let answerCommentSibling = null;
          if (this.element.parentElement) {
            answerCommentSibling = Array.from(this.element.parentElement.children).find(
              (el) => el.classList && el.classList.contains('answer_comment'),
            );
          }
          if (answerCommentSibling) {
            const removeClassesRecursively = (el) => {
              if (el.classList) {
                el.classList.remove('hidden', 'collapsed');
              }
              for (const child of el.children || []) {
                removeClassesRecursively(child);
              }
            };
            removeClassesRecursively(answerCommentSibling);
            answerCommentSibling.style.removeProperty('display');
            void answerCommentSibling.offsetHeight;
          }
        }
        if (this.hasDisclaimerTarget) {
          this.disclaimerTarget.style.display = 'none';
        }
      }, 100);
    };
    if (this._submitButton) {
      this._submitButton.addEventListener('click', this._handleSubmitClick);
    }
  }

  disconnect() {
    // Remove submit button click listener to prevent duplicates
    if (this._submitButton && this._handleSubmitClick) {
      this._submitButton.removeEventListener('click', this._handleSubmitClick);
    }
    this._submitButton = null;
    this._handleSubmitClick = null;

    if (this._summaryFeedbackAbortController) {
      this._suppressSummaryFeedbackAbortError = true;
      try {
        this._summaryFeedbackAbortController.abort();
      } catch (_) {
        /* ignore */
      }
      this._summaryFeedbackAbortController = null;
    }
  }

  disableSubmitIfNextContinueIsEnabledOrNextSectionIsVisible() {
    const submitButtonContainer = this.getSiblingSubmitButtonContainer();
    const submitButton = this.getSiblingButton();

    // Disable textarea
    if (this.hasTextareaTarget) {
      this.textareaTarget.disabled = true;
    }
    removeSpeechToTextToolbarFromContainer(this.element);

    if (submitButtonContainer) {
      const onClickValue = submitButtonContainer.getAttribute('data-button-action-on-click-value');

      if (onClickValue) {
        const actions = JSON.parse(onClickValue);
        const continueAction = actions.find(
          (a) =>
            a.type === 'action' &&
            a.arguments.key === 'inactive' &&
            a.arguments.value === 1 &&
            a.action === 'setNodeKVPair',
        );
        const continueButtonContainerUid = continueAction ? continueAction.arguments.uid : null;

        if (continueButtonContainerUid) {
          const continueButtonContainer = document.getElementById(continueButtonContainerUid);
          const continueButton =
            continueButtonContainer && continueButtonContainer.querySelector('button');

          if (
            continueButtonContainer &&
            continueButtonContainer.getAttribute('data-button-action-inactive-value') === '1' &&
            continueButton &&
            continueButton.innerText.trim().toLowerCase() === 'continue'
          ) {
            setTimeout(() => {
              submitButton.disabled = true;
            }, 100);
            this.disableRating();
          }

          const continueButtonActions = continueButtonContainer.getAttribute(
            'data-button-action-on-click-value',
          );
          if (continueButtonActions) {
            const continueActions = JSON.parse(continueButtonActions);
            const showSectionAction = continueActions.find(
              (a) =>
                a.type === 'action' &&
                a.action === 'setNodeKVPair' &&
                a.arguments.key === 'invisible' &&
                a.arguments.value === false,
            );
            const sectionToShowUid = showSectionAction ? showSectionAction.arguments.uid : null;
            if (sectionToShowUid) {
              const sectionToShowElement = document.getElementById(sectionToShowUid);
              if (sectionToShowElement && sectionToShowElement.offsetParent !== null) {
                setTimeout(() => {
                  submitButton.disabled = true;
                }, 0);
                this.disableRating();
              }
            }
          }
        }
      }
    }
  }

  getAnswerCommentSiblingElement() {
    if (!this.element.parentElement) return null;
    return (
      Array.from(this.element.parentElement.children).find(
        (el) => el.classList && el.classList.contains('answer_comment'),
      ) || null
    );
  }

  // Helper to get and memoize the submit button container sibling
  getSiblingSubmitButtonContainer() {
    if (
      this.siblingSubmitButtonContainer &&
      document.body.contains(this.siblingSubmitButtonContainer)
    ) {
      return this.siblingSubmitButtonContainer;
    }
    const root = this.hasContainerTarget ? this.containerTarget : this.element;
    const container = findSubmitButtonContainerForElement(root);
    this.siblingSubmitButtonContainer = container;
    return container;
  }

  getSiblingButton() {
    const buttonContainer = this.getSiblingSubmitButtonContainer();
    if (buttonContainer) {
      return buttonContainer.querySelector('button');
    }
    return null;
  }

  _backendAiSummaryRequestFeedbackValue() {
    const uid = this.nodeUidValue;
    const state = this.userSectionStateValue || {};
    const entry =
      state[uid] || state[uid.replace(/-/g, ' ')] || state[uid.replace(/ /g, '-')];
    return !!entry?.aiSummaryStatementRequestFeedback?.value;
  }

  async _persistAiSummaryResponseError(message) {
    const now = Date.now();
    const value = message == null || message === '' ? null : String(message);
    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [this.nodeUidValue]: {
                aiSummaryStatementResponseError: { value, updatedAt: now },
              },
            },
          },
        },
      });
      this.userSectionStateValue = getDusFromDom() || {};
    } catch (e) {
      console.error(e);
    }
  }

  _notesController() {
    const root = this.hasContainerTarget ? this.containerTarget : this.element;
    return this.application.getControllerForElementAndIdentifier(root, 'notes');
  }

  _syncNotesSubmitButtonState() {
    this._notesController()?.reconcileVisibleSubmitButton?.();
  }

  _detachDynamicTextareaListeners() {
    const ta = this.hasTextareaTarget ? this.textareaTarget : null;
    if (ta && this._dynamicButtonInputHandler) {
      ta.removeEventListener('input', this._dynamicButtonInputHandler);
      this._dynamicButtonInputHandler = null;
    }
    if (ta && this._noteSavedHandler) {
      ta.removeEventListener('note:saved', this._noteSavedHandler);
      this._noteSavedHandler = null;
    }
  }

  setAiFeedbackRequested(enabled, { persist = true } = {}) {
    const checked = !!enabled;
    this._aiFeedbackRequested = checked;

    if (checked) {
      const originalButtonContainer = this.getSiblingSubmitButtonContainer();
      if (originalButtonContainer) {
        originalButtonContainer.style.display = 'none';
        const origBtn = originalButtonContainer.querySelector('button');
        if (origBtn) origBtn.style.display = 'none';
      }
      this.addDynamicSubmitButton();
      this._syncNotesSubmitButtonState();
    } else {
      const originalButtonContainer = this.getSiblingSubmitButtonContainer();
      if (originalButtonContainer) {
        originalButtonContainer.style.display = '';
        const origBtn = originalButtonContainer.querySelector('button');
        if (origBtn) origBtn.style.display = '';
      }
      this.removeDynamicSubmitButton();
      this._syncNotesSubmitButtonState();
    }

    if (!persist) return;

    const backendVal = this._backendAiSummaryRequestFeedbackValue();
    if (backendVal !== checked) {
      const body = {
        document_user_state: {
          state: {
            [this.nodeUidValue]: {
              aiSummaryStatementRequestFeedback: {
                value: checked,
                updatedAt: Date.now(),
              },
            },
          },
        },
      };
      void updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body,
      }).then(() => {
        this.userSectionStateValue = getDusFromDom() || {};
      });
    }
  }

  addDynamicSubmitButton() {
    // Only add if not already present
    if (this.dynamicButton) {
      return;
    }
    if (this.hasDynamicButtonPlaceholderTarget) {
      this._detachDynamicTextareaListeners();
      this.dynamicButtonPlaceholderTarget.innerHTML =
        '<button type="button" class="ai-dynamic-submit doc-button">Submit</button>';
      this.dynamicButton =
        this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
      const originalButtonContainer = this.getSiblingSubmitButtonContainer();
      if (originalButtonContainer) {
        originalButtonContainer.style.display = 'none';
        const origBtn = originalButtonContainer.querySelector('button');
        if (origBtn) origBtn.style.display = 'none';
      }
      if (this.dynamicButton) {
        const textarea = this.textareaTarget;
        const hasText = !!(textarea && textarea.value && textarea.value.trim().length);
        const notesCtrl = this._notesController();
        const persistIdle = notesCtrl ? notesCtrl.isPersistIdle() : false;
        this.dynamicButton.disabled = !hasText || !persistIdle;
        if (textarea) {
          this._dynamicButtonInputHandler = () => {
            if (!textarea.value || !textarea.value.trim().length) {
              this.dynamicButton.disabled = true;
              return;
            }
            this.dynamicButton.disabled = true;
          };
          textarea.addEventListener('input', this._dynamicButtonInputHandler);
          this._noteSavedHandler = (ev) => {
            const saveSettled = noteSaveEpochsSettled(ev.detail || {});
            if (!textarea.value || !textarea.value.trim().length) {
              this.dynamicButton.disabled = true;
              return;
            }
            if (saveSettled) {
              this.dynamicButton.disabled = false;
            }
          };
          textarea.addEventListener('note:saved', this._noteSavedHandler);
        }
        this.dynamicButton.addEventListener('click', () => this.onDynamicSubmit());
      }
    }
  }

  removeDynamicSubmitButton() {
    this._detachDynamicTextareaListeners();
    if (this.dynamicButton) {
      this.dynamicButton.remove();
      this.dynamicButton = null;
    }
  }

  onDynamicSubmit() {
    this.infoModalTarget.style.display = 'block';
    requestAnimationFrame(() => {
      if (this.hasInfoModalOkayTarget) {
        this.infoModalOkayTarget.focus({ preventScroll: true });
      }
    });
  }

  closeInfoModal() {
    this.infoModalTarget.style.display = 'none';
    this.applyChromeForSummaryFeedbackLayout();
    this.showSummaryFeedbackLoading();
    this.removeDynamicSubmitButton();
    const originalButton = this.getSiblingSubmitButtonContainer();
    if (originalButton) originalButton.style.display = '';
    const submitButton = this.getSiblingButton();
    if (submitButton) submitButton.disabled = true;
    void this.requestAiSummaryFeedback();
  }

  recoverFromTurboStreamError() {
    this._recoverFromAiFeedbackError('', false);
  }

  applyChromeForSummaryFeedbackLayout() {
    if (this.hasTextareaTarget) {
      this.textareaTarget.style.display = 'none';
    }
    if (this.hasCharCountWrapperTarget) {
      this.charCountWrapperTarget.style.display = 'none';
    }
    if (this.hasGradedFeedbackTarget) {
      this.gradedFeedbackTarget.style.display = 'none';
    }
    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'none';
    }
  }

  showSummaryFeedbackLoading() {
    if (!this.hasSummaryFeedbackStreamTarget) return;
    const showDisclaimer = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    const raw = this.textareaTarget?.value || '';
    const userText = escapeHtml(raw);
    const len = raw.length;
    const lim = this.characterLimitValue;
    const disclaimerRow = showDisclaimer
      ? '<p class="ai-two-column-left-disclaimer rails-graded-feedback">Your response will be recorded in your Student Report and may also be viewed by faculty.</p>'
      : '';
    this.summaryFeedbackStreamTarget.innerHTML = `
      <div class="ai-two-column-wrapper">
        <h3 class="ai-two-column-left-heading">Your Summary Statement</h3>
        <h3 class="ai-two-column-right-heading">AI-Generated Feedback</h3>
        ${disclaimerRow}
        <div class="ai-two-column-right-spacer" aria-hidden="true"></div>
        <div class="ai-two-column-left-body">
          <textarea rows="5" disabled>${userText}</textarea>
          <span class="letter-count">Letter Count: ${len} / ${lim}</span>
        </div>
        <div class="ai-two-column-right-body">
          <textarea class="ai-feedback-box" rows="5" disabled>Loading AI feedback...</textarea>
        </div>
      </div>
    `;
  }

  renderStimulusTwoColumnFeedback(feedbackText) {
    if (!this.hasSummaryFeedbackStreamTarget) return;
    const showDisclaimer = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    const uid = this.nodeUidValue;
    const userRaw = this.textareaTarget?.value || '';
    const userEsc = escapeHtml(userRaw);
    const feedbackEsc = escapeHtml((feedbackText || '').toString());
    const lim = this.characterLimitValue;
    const len = userRaw.length;
    const currentRating = this.aiFeedbackState.userRating || 0;
    const ratingDisabled = !!this.aiFeedbackState.userRatingSubmitted;
    const smileySvgs = [
      `<img src="${smile1}" alt="Very Dissatisfied" />`,
      `<img src="${smile2}" alt="Dissatisfied" />`,
      `<img src="${smile3}" alt="Neutral" />`,
      `<img src="${smile4}" alt="Satisfied" />`,
      `<img src="${smile5}" alt="Very Satisfied" />`,
    ];
    const smileyRatingBar = `
      <div class="ai-smile-rating-bar">
        ${[5, 4, 3, 2, 1]
          .map(
            (val) => `
          <button type="button" class="aq-smile-rating-button ai-smile-rating-btn${currentRating === val ? ' active' : ''}" data-rating="${val}" ${ratingDisabled ? 'disabled' : ''}>${smileySvgs[val - 1]}</button>
        `,
          )
          .join('')}
      </div>
    `;
    const commentRaw = this.aiFeedbackState.userComment
      ? String(this.aiFeedbackState.userComment).trim()
      : '';
    const commentEsc = escapeHtml(commentRaw);

    const disclaimerRow = showDisclaimer
      ? '<p class="ai-two-column-left-disclaimer rails-graded-feedback">The suggested answer is shown below.</p>'
      : '';

    this.summaryFeedbackStreamTarget.innerHTML = `
      <div class="ai-two-column-wrapper">
        <h3 class="ai-two-column-left-heading" for="text-area-${uid}">Your Summary Statement</h3>
        <h3 class="ai-two-column-right-heading" for="ai-feedback-${uid}">AI-Generated Feedback</h3>
        ${disclaimerRow}
        <div class="ai-two-column-right-spacer" aria-hidden="true"></div>
        <div class="ai-two-column-left-body">
          <textarea id="text-area-${uid}" rows="5" disabled>${userEsc}</textarea>
          <span class="letter-count">Letter Count: ${len} / ${lim}</span>
        </div>
        <div class="ai-two-column-right-body">
          <textarea id="ai-feedback-${uid}" class="ai-feedback-box" rows="5" disabled>${feedbackEsc}</textarea>
        </div>
      </div>
      <div class="answer-comment-placeholder"></div>
      <div class="ai-rating-container">
        <hr/>
        <div class="ai-rating-label" role="group" aria-labelledby="ai-rating-prompt-${uid}">
          <p id="ai-rating-prompt-${uid}">
            My reaction to this feedback is best expressed as: <span style="color: red;">*</span>
          </p>
          ${smileyRatingBar}
        </div>
        <label class="ai-rating-comment-label" for="ai-comment-${uid}">
          How could this feedback process be improved:
          <textarea id="ai-comment-${uid}" class="ai-comment" rows="3" maxlength="${lim}" ${ratingDisabled ? 'disabled' : ''}>${commentEsc}</textarea>
        </label>
      </div>
    `;
  }

  resolveIdealSummaryText() {
    let ideal = '';
    const fromMemory = this.getAnswerCommentRootFromMemory();
    if (fromMemory) {
      ideal = this.extractIdealFromAnswerCommentRoot(fromMemory);
    } else {
      const answerCommentSibling = this.getAnswerCommentSiblingElement();
      if (answerCommentSibling) {
        ideal = this.extractIdealFromAnswerCommentRoot(answerCommentSibling);
      }
    }
    return ideal;
  }

  async requestAiSummaryFeedback() {
    if (this._summaryFeedbackFetchInFlight) return;
    if (seemsOffline()) {
      this._recoverFromAiFeedbackError('', true);
      return;
    }
    this._summaryFeedbackFetchInFlight = true;
    this.aiFeedbackState.loading = true;

    const abortController = new AbortController();
    this._summaryFeedbackAbortController = abortController;
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, MAX_SUMMARY_STATEMENT_TIME_MS);

    const ideal = this.resolveIdealSummaryText();
    const userSummary = this.textareaTarget?.value || '';
    const answerCommentUid =
      this.getAnswerCommentIdFromStoredHtml() || this.getAnswerCommentSiblingElement()?.id || '';

    const token = getCsrfToken();
    const body = new URLSearchParams();
    body.append('authenticity_token', token);
    body.append('ideal_summary_statement', ideal);
    body.append('user_summary_statement', userSummary);
    body.append('node_uid', this.nodeUidValue);
    body.append('character_limit', String(this.characterLimitValue));
    if (answerCommentUid) body.append('answer_comment_uid', answerCommentUid);

    try {
      const res = await fetch(
        `/documents/${encodeURIComponent(String(this.documentIdValue))}/automated_summary_statement_feedback.json`,
        {
          method: 'POST',
          redirect: 'manual',
          signal: abortController.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRF-Token': token,
          },
          body,
        },
      );
      const text = await res.text();
      let payload = null;
      if (text) {
        try {
          payload = JSON.parse(text);
        } catch {
          payload = null;
        }
      }

      if (!res.ok) {
        this.aiFeedbackState.loading = false;
        let errMsg = `Request failed (${res.status})`;
        if (payload && typeof payload === 'object' && payload.error) {
          errMsg = String(payload.error);
        } else if (payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
          errMsg = String(payload.errors[0]);
        } else if (payload && typeof payload === 'object' && payload.message != null) {
          errMsg = String(payload.message);
        } else if (typeof payload === 'string' && payload.trim()) {
          errMsg = payload.trim();
        } else if (payload && typeof payload === 'object') {
          errMsg = JSON.stringify(payload).slice(0, 500);
        } else if (text && text.trim()) {
          errMsg = text.trim().slice(0, 500);
        }
        this._recoverFromAiFeedbackError(errMsg);
        return;
      }

      if (!payload || typeof payload !== 'object') {
        this.aiFeedbackState.loading = false;
        this._recoverFromAiFeedbackError('Invalid response from server');
        return;
      }

      const aiResponse = payload.ai_response != null ? String(payload.ai_response) : '';

      this.aiFeedbackState.loading = false;
      this.aiFeedbackState.received = true;
      this.aiFeedbackState.error = null;
      this.aiFeedbackState.feedback = aiResponse;
      void this._persistAiSummaryResponseError(null);
      this.renderStimulusTwoColumnFeedback(aiResponse);

      this.finalizeAnswerCommentPlacement({ skipDocumentUserStateSync: true });

      this.configureSubmitAfterFeedback();
      this.autoResizeFeedbackTextarea(this.aiSummaryFeedbackBlockTarget);
      if (!this.aiFeedbackState.userRatingSubmitted) {
        this.enableRating();
      } else {
        this.disableRating();
      }
    } catch (err) {
      console.error(err);
      this.aiFeedbackState.loading = false;
      if (err?.name === 'AbortError') {
        if (this._suppressSummaryFeedbackAbortError) {
          this._suppressSummaryFeedbackAbortError = false;
          return;
        }
        this._recoverFromAiFeedbackError('Connection Timeout');
        return;
      }
      this._recoverFromAiFeedbackError(err?.message || 'Network error');
    } finally {
      clearTimeout(timeoutId);
      this._summaryFeedbackAbortController = null;
      this._summaryFeedbackFetchInFlight = false;
    }
  }

  finalizeAnswerCommentPlacement(options = {}) {
    const { skipDocumentUserStateSync = false } = options;
    const container = this.aiSummaryFeedbackBlockTarget;
    if (!container) return;

    let liveAnswerComment = null;
    if (this.element.parentElement) {
      liveAnswerComment = Array.from(this.element.parentElement.children).find(
        (el) => el.classList && el.classList.contains('answer_comment'),
      );
    }
    const parsedRoot = this.getAnswerCommentRootFromMemory();
    let answerCommentId = parsedRoot?.id || null;
    let answerCommentBody = parsedRoot?.querySelector('.doc-section-body') || null;
    if (!answerCommentBody && liveAnswerComment) {
      answerCommentBody = liveAnswerComment.querySelector('.doc-section-body');
      answerCommentId = liveAnswerComment.id || answerCommentId;
    }
    const placeholder = container.querySelector('.answer-comment-placeholder');
    const answerCommentHeading = `
        <div class="doc-section-header-title">
          <div class="answer-comment-title-container">
            <h4 class="doc-section-header-title-heading" style="font-weight: inherit;">
              Answer Comment
            </h4>
          </div>
        </div>
      `;
    if (answerCommentBody && placeholder && !placeholder.querySelector('.doc-section-body')) {
      placeholder.insertAdjacentHTML('afterbegin', answerCommentHeading);
      const importedBody = document.importNode(answerCommentBody, true);
      placeholder.append(importedBody);

      if (liveAnswerComment) {
        liveAnswerComment.style.setProperty('display', 'none', 'important');
      }

      const gradedAlreadySet =
        this.feedbackStateValue &&
        this.feedbackStateValue.graded &&
        this.feedbackStateValue.graded.value === true;
      const invisibleAlreadySet =
        !!(
          this.feedbackStateValue &&
          answerCommentId &&
          this.userSectionStateValue?.[answerCommentId]?.invisible?.value === false
        );
      if (
        !skipDocumentUserStateSync &&
        answerCommentId &&
        (!gradedAlreadySet || !invisibleAlreadySet)
      ) {
        const now = Date.now();
        const stateUpdate = {};
        if (!invisibleAlreadySet) {
          stateUpdate[answerCommentId] = {
            invisible: {
              value: false,
              updatedAt: now,
            },
          };
        }
        if (!gradedAlreadySet) {
          stateUpdate[this.nodeUidValue] = {
            graded: {
              value: true,
              updatedAt: now,
            },
          };
        }
        const body = {
          document_user_state: {
            state: stateUpdate,
          },
        };
        updateDocumentUserState({
          documentId: this.documentIdValue,
          userId: this.userIdValue,
          body,
        });
      }

      if (answerCommentId) {
        this.removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentId);
      }
    }
  }

  configureSubmitAfterFeedback() {
    const container = this.aiSummaryFeedbackBlockTarget;
    if (!container || !container.querySelector('.ai-rating-container')) return;
    if (this.dynamicButton) {
      this.removeDynamicSubmitButton();
    }
    const originalButtonContainer = this.getSiblingSubmitButtonContainer();
    if (originalButtonContainer) {
      originalButtonContainer.style.display = '';
      const origBtn = originalButtonContainer.querySelector('button');
      if (origBtn) origBtn.style.display = '';
    }
    const submitButton = this.getSiblingButton();
    const ratingInput = container.querySelector('.ai-smile-rating-bar');
    if (submitButton && ratingInput) {
      setTimeout(() => {
        const ratingGiven = this.aiFeedbackState.userRating && this.aiFeedbackState.userRating > 0;
        submitButton.disabled = !ratingGiven;
      }, 100);
    }
  }

  autoResizeFeedbackTextarea(container) {
    const feedbackTextarea = container.querySelector('.ai-feedback-box');
    if (!feedbackTextarea) return;
    const setHeight = () => {
      feedbackTextarea.style.height = 'auto';
      feedbackTextarea.style.height = `${Math.max(feedbackTextarea.scrollHeight, 120)}px`;
    };
    setHeight();
  }

  removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentId) {
    const submitButtonContainer = this.getSiblingSubmitButtonContainer();
    if (submitButtonContainer) {
      const attr = submitButtonContainer.getAttribute('data-button-action-on-click-value');
      if (attr) {
        let arr;
        try {
          arr = JSON.parse(attr);
        } catch {
          return;
        }
        const filteredArr = Array.isArray(arr)
          ? arr.filter(
              (obj) =>
                !(
                  obj &&
                  obj.action === 'setNodeKVPair' &&
                  obj.arguments &&
                  obj.arguments.key === 'invisible' &&
                  obj.arguments.uid === answerCommentId
                ),
            )
          : arr;
        submitButtonContainer.setAttribute(
          'data-button-action-on-click-value',
          JSON.stringify(filteredArr),
        );
        submitButtonContainer.setAttribute(
          'data-button-action-ai-summary-statement-answer-comment-shown-value',
          true,
        );
        // Force update the button-action controller value
        const buttonController = this.application.getControllerForElementAndIdentifier(
          submitButtonContainer,
          'button-action',
        );
        if (buttonController) {
          buttonController.onClickValue = filteredArr;
        }
      }
    }
  }

  showErrorModal() {
    if (!this.hasErrorModalTarget && this._errorModalHtml) {
      this.element.insertAdjacentHTML('beforeend', this._errorModalHtml);
    }
    const modal = this.element.querySelector('.ai-error-modal');
    if (!modal) return;
    const message = modal.querySelector('[data-ai-summary-feedback-target="errorMessage"]');
    if (message) {
      message.innerText = seemsOffline()
        ? OFFLINE_AI_NOTICE_BODY
        : 'Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still submit your summary statement and continue working through the case.';
    }
    modal.style.display = 'block';
  }

  closeErrorModal() {
    this.errorModalTarget.style.display = 'none';
    this._aiFeedbackRequested = false;
    this.setAiFeedbackRequested(false);
    const originalButton = this.getSiblingSubmitButtonContainer();
    if (originalButton) originalButton.style.display = '';
  }

  disableRating() {
    const container = this.aiSummaryFeedbackBlockTarget;
    // Remove event listeners from rating buttons
    this._ratingBtns = Array.from(container.querySelectorAll('.aq-smile-rating-button'));
    if (!this._ratingBtnHandlers) this._ratingBtnHandlers = new Map();
    for (const btn of this._ratingBtns) {
      btn.disabled = true;
      const handler = this._ratingBtnHandlers.get(btn);
      if (handler) {
        btn.removeEventListener('click', handler);
      }
    }
    // Remove event listener from comment input
    const commentInput = container.querySelector('.ai-comment');
    if (commentInput) {
      commentInput.disabled = true;
      if (this._commentInputHandler) {
        commentInput.removeEventListener('input', this._commentInputHandler);
      }
    }
  }

  enableRating() {
    const container = this.aiSummaryFeedbackBlockTarget;
    this._ratingBtns = Array.from(container.querySelectorAll('.aq-smile-rating-button'));
    this._submitButton = this.getSiblingButton();
    if (!this._ratingBtnHandlers) this._ratingBtnHandlers = new Map();
    for (const btn of this._ratingBtns) {
      btn.disabled = false;
      // Remove any previous handler to avoid duplicates
      const prevHandler = this._ratingBtnHandlers.get(btn);
      if (prevHandler) {
        btn.removeEventListener('click', prevHandler);
      }
      // Attach new handler
      const handler = () => {
        const val = parseInt(btn.getAttribute('data-rating'), 10);
        this.aiFeedbackState.userRating = val;
        if (this._submitButton) this._submitButton.disabled = false;
        // add active class to the selected button and remove from others
        this._ratingBtns.forEach((b) => {
          if (b === btn) {
            b.classList.add('active');
          } else {
            b.classList.remove('active');
          }
        });
        btn.focus({ preventScroll: true });
      };
      btn.addEventListener('click', handler);
      this._ratingBtnHandlers.set(btn, handler);
    }
    // Reattach comment input handler
    const commentInput = container.querySelector('.ai-comment');
    if (commentInput) {
      commentInput.disabled = false;
      // Remove previous handler if present
      if (this._commentInputHandler) {
        commentInput.removeEventListener('input', this._commentInputHandler);
      }
      // Attach new handler
      this._commentInputHandler = (() => {
        return function () {
          this.aiFeedbackState.userComment = commentInput.value;
        }.bind(this);
      })();
      commentInput.addEventListener('input', this._commentInputHandler);
    }
  }

  _recoverFromAiFeedbackError(errorMessage, showErrorModal = true) {
    if (errorMessage) {
      void this._persistAiSummaryResponseError(errorMessage);
    }
    if (showErrorModal) {
      this.showErrorModal();
    }
    this.aiFeedbackState.requested = false;
    this.aiFeedbackState.loading = false;
    this.aiFeedbackState.received = false;
    this.aiFeedbackState.error = errorMessage;
    this.aiFeedbackState.feedback = '';

    if (this.hasAiSummaryFeedbackBlockTarget) {
      const streamId = `ai-summary-feedback-stream-${this.nodeUidValue}`;
      this.aiSummaryFeedbackBlockTarget.innerHTML = `
        <div id="${streamId}" class="ai-summary-feedback-stream-root" data-ai-summary-feedback-target="summaryFeedbackStream"></div>
      `;
    }
    this.textareaTarget.style.display = '';
    if (this.hasCharCountWrapperTarget) {
      this.charCountWrapperTarget.style.display = '';
    }
    this._aiFeedbackRequested = false;
    this.setAiFeedbackRequested(false);

    if (docBodyRequiresAnswerCommentInDom(this.element)) {
      const ac = this.element.parentElement
        ? Array.from(this.element.parentElement.children).find(
            (el) => el.classList && el.classList.contains('answer_comment'),
          )
        : null;
      if (ac) ac.style.removeProperty('display');
    } else {
      removeAnswerCommentFromUI(this.element);
    }

    const siblingButton = this.getSiblingButton();
    if (siblingButton) {
      siblingButton.disabled = !(
        this.textareaTarget.value && this.textareaTarget.value.trim().length > 0
      );
    }
    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'block';
    }
  }
}
